/**
 * IconBlockRenderer — icon-block
 * Icon + heading + optional body text.
 * In editing mode the icon container is clickable and opens a library picker.
 */
import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import i18n from "i18next";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { Eyebrow } from "../../ui/Eyebrow";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";
import { ICONS as LIBRARY_ICONS } from "../../../../_editor/components/carousel/library/IconsPanel";

function IconPickerPopup({ anchorRect, onSelect, onClose }) {
  const [search, setSearch] = useState("");
  const popupRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return LIBRARY_ICONS;
    return LIBRARY_ICONS.filter(
      (ic) =>
        ic.name.toLowerCase().includes(q) || (ic.keywords && ic.keywords.toLowerCase().includes(q))
    );
  }, [search]);

  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const popupHeight = 300;
  const top = spaceBelow >= popupHeight ? anchorRect.bottom + 6 : anchorRect.top - popupHeight - 6;
  const left = Math.min(anchorRect.left, window.innerWidth - 296);

  useEffect(() => {
    function onDocClick(e) {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    }
    const timeout = setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
    }, 10);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [onClose]);

  const popup = (
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top,
        left,
        zIndex: 99999,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 10px 6px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
        width: 280,
        maxHeight: popupHeight,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        autoFocus
        placeholder={i18n.t("search") || "Search…"}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          height: 32,
          padding: "0 10px",
          borderRadius: 999,
          border: "1px solid #e5e7eb",
          outline: "none",
          fontSize: 12,
          backgroundColor: "#f9fafb",
          boxSizing: "border-box",
        }}
      />
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
          gap: 5,
          paddingRight: 2,
        }}
      >
        {filtered.map((icon) => (
          <button
            key={icon.id}
            type="button"
            title={icon.name}
            onClick={() => {
              onSelect(icon.url);
              onClose();
            }}
            style={{
              border: "1px solid transparent",
              borderRadius: 6,
              padding: 4,
              background: "#f3f4f6",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              aspectRatio: "1/1",
              transition: "background 0.12s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#e0d2ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          >
            <img
              src={icon.url}
              alt={icon.name}
              style={{ maxWidth: "75%", maxHeight: "75%", objectFit: "contain" }}
            />
          </button>
        ))}
      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
}

function IconSlot({ icon, iconUrl, iconSize, editing, onIconChange, slotSize = "4em" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const slotRef = useRef(null);

  return (
    <div
      ref={slotRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: slotSize,
        height: slotSize,
        borderRadius: "var(--radius-lg)",
        background: "var(--color-bg-surface-strong)",
        border: editing
          ? "2px dashed var(--color-text-accent, #7c3aed)"
          : "var(--stroke-thin) solid var(--color-border-subtle)",
        cursor: editing ? "pointer" : "default",
        flexShrink: 0,
        transition: "border 0.15s, box-shadow 0.15s",
        boxShadow: editing ? "0 0 0 3px rgba(124,58,237,0.12)" : undefined,
      }}
      onClick={(e) => {
        if (editing) {
          e.stopPropagation();
          const rect = slotRef.current?.getBoundingClientRect();
          setAnchorRect(rect);
          setPickerOpen((p) => !p);
        }
      }}
      title={editing ? i18n.t("messages.change_icon") || "Change icon" : undefined}
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt={icon || "icon"}
          style={{
            maxWidth: "72%",
            maxHeight: "72%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      ) : (
        <svg
          width={iconSize || "2em"}
          height={iconSize || "2em"}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{ color: "var(--color-text-accent, #7c3aed)", opacity: 0.4 }}
        >
          <rect
            x="2"
            y="2"
            width="20"
            height="20"
            rx="4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
          <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )}

      {editing && (
        <span
          style={{
            position: "absolute",
            bottom: 2,
            right: 2,
            fontSize: 10,
            background: "rgba(124,58,237,0.85)",
            color: "#fff",
            borderRadius: 4,
            padding: "1px 4px",
            pointerEvents: "none",
            lineHeight: 1.4,
          }}
        >
          ✎
        </span>
      )}

      {pickerOpen && anchorRect && (
        <IconPickerPopup
          anchorRect={anchorRect}
          onSelect={(url) => onIconChange(url)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// Each sub-field (eyebrow / title / body) wraps in its OWN AnimatedField
// so the AnimationPanel's per-field selection drives only that field.
const LAYOUTS = {
  centered: ({
    icon,
    iconUrl,
    iconSize,
    eyebrowNode,
    headingNode,
    bodyNode,
    editing,
    onChange,
  }) => (
    <div
      style={{
        display: "grid",
        gap: "var(--space-3)",
        justifyItems: "center",
        textAlign: "center",
      }}
    >
      <IconSlot
        icon={icon}
        iconUrl={iconUrl}
        iconSize={iconSize}
        editing={editing}
        onIconChange={(url) => onChange("iconUrl", url)}
      />
      {eyebrowNode}
      {headingNode}
      {bodyNode}
    </div>
  ),

  row: ({ icon, iconUrl, iconSize, eyebrowNode, headingNode, bodyNode, editing, onChange }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "4em 1fr",
        gap: "var(--space-4)",
        alignItems: "start",
      }}
    >
      <IconSlot
        icon={icon}
        iconUrl={iconUrl}
        iconSize={iconSize}
        editing={editing}
        onIconChange={(url) => onChange("iconUrl", url)}
      />
      <div style={{ display: "grid", gap: "var(--space-2)" }}>
        {eyebrowNode}
        {headingNode}
        {bodyNode}
      </div>
    </div>
  ),

  inline: ({ icon, iconUrl, iconSize, eyebrowNode, headingNode, bodyNode, editing, onChange }) => (
    <div style={{ display: "grid", gap: "var(--space-2)" }}>
      {eyebrowNode}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <IconSlot
          icon={icon}
          iconUrl={iconUrl}
          iconSize={iconSize || "1.4em"}
          slotSize="2.5em"
          editing={editing}
          onIconChange={(url) => onChange("iconUrl", url)}
        />
        {headingNode}
      </div>
      {bodyNode}
    </div>
  ),
};

export function IconBlockRenderer({
  element,
  editing,
  onChange,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const icon = element.icon || "star";
  const iconUrl = element.iconUrl || null;
  const layout = element.layout || "row";
  const iconSize =
    typeof element.iconSize === "number" ? `${element.iconSize / 14}em` : element.iconSize || null;

  const eyebrowField = element.eyebrow;
  const headingField = element.heading || element.title;
  const bodyField = element.body;

  const eyebrow = eyebrowField ? resolveTextOptional(eyebrowField) : null;
  const heading = headingField ? resolveText(headingField) : null;
  const body = bodyField ? resolveTextOptional(bodyField) : null;

  const fieldAnim = (f) => [
    typeof f === "object" && f != null ? (f.boxAnimation ?? null) : null,
    typeof f === "object" && f != null ? (f.boxExitAnimation ?? null) : null,
  ];
  const [eyebrowBox, eyebrowExit] = fieldAnim(eyebrowField);
  const [headingBox, headingExit] = fieldAnim(headingField);
  const [bodyBox, bodyExit] = fieldAnim(bodyField);

  const headingKey = element.heading != null ? "heading" : "title";

  const eyebrowNode =
    eyebrow !== null || editing ? (
      <AnimatedField
        boxAnimation={eyebrowBox}
        boxExitAnimation={eyebrowExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <Eyebrow>
          <EditableText
            value={eyebrow || ""}
            editing={editing}
            onChange={(val) => onChange("eyebrow", val)}
          />
        </Eyebrow>
      </AnimatedField>
    ) : null;

  const headingNode =
    heading || editing ? (
      <AnimatedField
        boxAnimation={headingBox}
        boxExitAnimation={headingExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <Title size="xl">
          <EditableText
            value={heading || ""}
            editing={editing}
            onChange={(val) => onChange(headingKey, val)}
          />
        </Title>
      </AnimatedField>
    ) : null;

  const bodyNode =
    body || editing ? (
      <AnimatedField
        boxAnimation={bodyBox}
        boxExitAnimation={bodyExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <Text role="body">
          <EditableText
            value={body || ""}
            editing={editing}
            onChange={(val) => onChange("body", val)}
          />
        </Text>
      </AnimatedField>
    ) : null;

  const LayoutFn = LAYOUTS[layout] || LAYOUTS.row;

  return (
    <SurfaceCard>
      <LayoutFn
        icon={icon}
        iconUrl={iconUrl}
        iconSize={iconSize}
        eyebrowNode={eyebrowNode}
        headingNode={headingNode}
        bodyNode={bodyNode}
        editing={editing}
        onChange={onChange}
      />
    </SurfaceCard>
  );
}

export default IconBlockRenderer;
